import { Button, SimpleGrid, Stack, useDisclosure } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { prettyCurrency, prettyDate } from '@pelicargo/utils';
import { useMemo } from 'react';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi2';

import { QuoteLineItem } from '../../utils/types';
import { LabeledText } from '../LabeledText';

type SurchargeDetailProps = {
  lineItem: QuoteLineItem;
};

export const SurchargeDetail = ({ lineItem }: SurchargeDetailProps) => {
  const { isOpen, onToggle } = useDisclosure();

  const isConnected = useMemo(() => {
    return lineItem?.disassociated_at === null;
  }, [lineItem?.disassociated_at]);

  const surcharge = useMemo(() => lineItem?.issued_by_surcharge, [lineItem?.issued_by_surcharge]);

  if (!surcharge) return null;

  return (
    <Stack direction="column" spacing="2">
      <Stack direction="column" spacing={0}>
        <Stack direction="column" spacing={3} p={3} flex={1}>
          <SimpleGrid columns={3} rowGap={3} columnGap={2} flex={1}>
            <LabeledText
              flex={1}
              label="Status"
              value={isConnected ? 'Connected' : 'Disconnected'}
              color={isConnected ? 'success' : 'error'}
            />
            <LabeledText
              flex={1}
              label="Starts At"
              value={prettyDate(surcharge.starts_at, {
                timeZone: 'Etc/UTC',
              })}
            />
            <LabeledText
              flex={1}
              label="Expires At"
              value={prettyDate(surcharge.expires_at, {
                timeZone: 'Etc/UTC',
              })}
            />
          </SimpleGrid>
          <LabeledText label="Comments" value={surcharge.comments} />
        </Stack>

        <SimpleGrid columns={3} rowGap={3} columnGap={2} p={3} borderTopWidth={1}>
          <LabeledText flex={1} label="Min Cost" value={prettyCurrency(surcharge.min_cost)} />
          <LabeledText flex={1} label="Per Unit Cost" value={prettyCurrency(surcharge.per_unit_cost)} />
          <LabeledText label="Unit" value={String(surcharge.unit)} />
        </SimpleGrid>

        <Show if={isOpen}>
          <SimpleGrid columns={3} rowGap={3} columnGap={2} p={3} borderTopWidth={1}>
            <LabeledText label="ID" value={String(surcharge.id || '')} />
            <LabeledText label="Optional" value={surcharge.is_optional ? 'Yes' : 'No'} />

            <LabeledText label="Category" value={surcharge.surcharge_category} />
            <LabeledText label="Cargo Type" value={surcharge.cargo_type} />
            <LabeledText label="Service Type" value={surcharge.service_type} />
            <LabeledText label="Freight Service" value={surcharge.freight_service} />

            <LabeledText label="Airline Product" value={surcharge.airline_product} />
            <LabeledText label="Airline label" value={surcharge.airline_label} />
            <LabeledText label="Airline Code" value={surcharge.airline_label} />
            <LabeledText label="Per Unit Cost Type" value={surcharge.per_unit_cost_type} />
            <LabeledText label="Weight Type" value={surcharge.weight_type} />
          </SimpleGrid>
        </Show>
      </Stack>
      <Button
        size="sm"
        variant="ghost"
        onClick={onToggle}
        colorScheme="brand"
        alignSelf="center"
        rightIcon={isOpen ? <HiChevronUp /> : <HiChevronDown />}
      >
        {isOpen ? 'Minimize' : 'Expand'}
      </Button>
    </Stack>
  );
};
